import { formatDate } from '../../helpers';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type { AgentFinancialResponse } from '../events-client/events-client.type';
import type { MinifiedTask } from '../tasks-client/task-client.type';
import type {
  AgentSpending,
  AgentSpendingBulkUpsertRequest,
  AllocationOfCostsChartResponse,
  AllocationOfCostsChartResponseByCategory,
  CostByInitiativeResponse,
  CostByInitiativeV2Response,
  CostVsBudgetChartResponse,
  Spending,
  SpendingDetail,
} from './financials-client.type';

const axiosInstance = AxiosInstance();
const baseURL = appConfig.VITE_APP_API_URL;

/**
 * Method to fetch the spending data for a portfolio
 *
 * @param {string} portfolioId portfolio id
 * @param {string} years years for which the spending data is to be fetched
 * @returns {Spending[]} spending data for the portfolio
 */
function getSpending(portfolioId: string, years?: string): Promise<Spending[]> {
  let apiURL = baseURL + `/api/financials/spending/?portfolio_id=${portfolioId}`;
  if (years !== undefined && years !== null && years !== '') {
    apiURL += `&years=${years}`;
  }
  return axiosInstance
    .get<Spending[]>(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response }));
}

/**
 * Method for upserting the spending data for a portfolio
 *
 * @param {Spending[]} spending list of spending data to be upserted
 * @returns {Spending[]} updated spending data
 */
function bulkUpsertSpendingV1(spending: Spending[]): Promise<Spending[]> {
  const apiURL = baseURL + `/api/financials/spending/`;
  const postData: {
    portfolio_id: string;
    year: number;
    projects: { [key: string]: { [key: string]: SpendingDetail[] } };
  } = {
    portfolio_id: '',
    year: 0,
    projects: {},
  };
  for (const item of spending) {
    if (!postData.portfolio_id) {
      postData.portfolio_id = item.portfolio_id;
    }
    if (!postData.year) {
      postData.year = item.year;
    }
    if (!postData.projects[item.project_id]) {
      postData.projects[item.project_id] = {};
    }

    if (item.subproject_id) {
      if (!postData.projects[item.project_id][item.subproject_id]) {
        postData.projects[item.project_id][item.subproject_id] = [];
      }
      postData.projects[item.project_id][item.subproject_id].push({
        month: item.month,
        budget: item.budget,
      });
    } else {
      if (!postData.projects[item.project_id]['self']) {
        postData.projects[item.project_id]['self'] = [];
      }
      postData.projects[item.project_id]['self'].push({
        month: item.month,
        budget: item.budget,
      });
    }
  }
  return axiosInstance
    .post(apiURL, postData)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response }));
}

/**
 * Method for upserting the spending data for a portfolio
 *
 * @param {Object} payload payload to be upserted
 * @returns {Spending[]} updated spending data
 */
function bulkUpsertSpending(payload: {
  portfolio_id: string;
  year: number;
  projects: { [key: string]: { [key: string]: SpendingDetail[] } };
}): Promise<Spending[]> {
  const apiURL = baseURL + `/api/financials/spending/`;

  return axiosInstance
    .post(apiURL, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response }));
}

/**
 * Method to fetch data for cost vs output chart
 *
 * @param {string} portfolioId portfolio id
 * @param {string} startDate start date
 * @param {string} endDate end date
 * @param {string} projectId project id
 * @param {string} subprojectId subproject id
 * @param {string} workPeriodIds work period ids if any selected
 * @param {boolean} force_recalculation force recalculation
 * @param {string} organizationId organization id
 * @returns {CostVsBudgetChartResponse} an object of cost vs output data
 */
async function getCostVsOutputChartData(
  startDate: Date,
  endDate: Date,
  portfolioId?: string | null,
  projectId?: string,
  subprojectId?: string,
  workPeriodIds?: string,
  force_recalculation?: boolean,
  organizationId?: string,
): Promise<CostVsBudgetChartResponse> {
  try {
    const start_date = formatDate(startDate);
    const end_date = formatDate(endDate);
    let apiURL =
      baseURL +
      `/api/work-periods/measure/?measure_name=cost_vs_output&portfolio_id=${portfolioId}&start_date=${start_date}&end_date=${end_date}&time_allocation_type=monthly`;
    if (projectId !== undefined) {
      apiURL += `&project_id=${projectId}`;
    }
    if (subprojectId !== undefined) {
      apiURL += `&subproject_id=${subprojectId}`;
    }
    if (workPeriodIds !== undefined) {
      apiURL += `&work_period_ids=${workPeriodIds}`;
    }
    if (organizationId !== undefined) {
      apiURL += `&organization_id=${organizationId}`;
    }
    apiURL += `&force_recalculation=${force_recalculation}`;

    const response = await axiosInstance.get<CostVsBudgetChartResponse>(apiURL);
    return response.data;
  } catch (error: unknown) {
    return Promise.reject({ error: error instanceof Error ? error.message : 'An unknown error occurred' });
  }
}

/**
 * Endpoint to fetch data for allocation of costs chart
 *
 * @param {string} portfolioId selected portfolio id
 * @param {string} startDate selected start date
 * @param {string} endDate selected end date
 * @param {string} projectId project id if any selected
 * @param {string} subprojectId subproject id if any selected
 * @param {string} workPeriodIds work period ids if any selected
 * @param {boolean} force_recalculation force recalculation
 * @param {string} organizationId organization id
 * @returns {AllocationOfCostsChartResponseByCategory} dictionary of spending data
 */
function getAllocationOfCostsChartData(
  portfolioId: string | null,
  startDate: Date,
  endDate: Date,
  projectId?: string | null,
  subprojectId?: string | null,
  workPeriodIds?: string | null,
  force_recalculation?: boolean,
  organizationId?: string,
): Promise<AllocationOfCostsChartResponseByCategory> {
  const start_date = formatDate(startDate);
  const end_date = formatDate(endDate);
  const params = new URLSearchParams({
    measure_name: 'cost_allocation',
    start_date: start_date,
    end_date: end_date,
    time_allocation_type: 'monthly',
  });

  // Only add portfolio_id if projectId is not provided
  if (projectId) {
    params.append('project_id', projectId);
  } else if (portfolioId) {
    params.append('portfolio_id', portfolioId);
  }

  if (subprojectId) {
    params.append('subproject_id', subprojectId);
  }

  if (workPeriodIds) {
    params.append('work_period_ids', workPeriodIds);
  }

  if (organizationId) {
    params.append('organization_id', organizationId);
  }

  params.append('force_recalculation', String(force_recalculation));

  const apiURL = `${baseURL}/api/work-periods/measure/?${params.toString()}`;
  return axiosInstance
    .get<AllocationOfCostsChartResponse>(apiURL)
    .then((response) => response.data.cost_allocation)
    .catch((error) => Promise.reject({ error: error.response }));
}

/**
 * Endpoint to fetch data for cost by initiative chart
 *
 * @param {string} portfolioId selected portfolio id
 * @param {string} startDate selected start date
 * @param {string} endDate selected end date
 * @param {string} projectId (OPTIONAL) project id if any selected
 * @param {string} subprojectId (OPTIONAL) subproject id if any selected
 * @param {string} workPeriodIds (OPTIONAL) work period ids if any selected
 * @param {boolean} force_recalculation force recalculation
 * @param {string} organizationId organization id
 * @returns {CostByInitiativeResponse[]} dictionary of spending data
 */
function getCostByInitiativeChartData(
  portfolioId: string,
  startDate: Date,
  endDate: Date,
  projectId?: string,
  subprojectId?: string,
  workPeriodIds?: string,
  force_recalculation?: boolean,
  organizationId?: string,
): Promise<CostByInitiativeResponse[]> {
  const start_date = formatDate(startDate);
  const end_date = formatDate(endDate);

  const versionURL = `/api/work-periods/measure/?measure_name=cost_by_initiative&portfolio_id=${portfolioId}&start_date=${start_date}&end_date=${end_date}&time_allocation_type=monthly`;
  let apiURL = baseURL + versionURL;
  if (projectId) {
    apiURL += `&project_id=${projectId}`;
  }
  if (subprojectId) {
    apiURL += `&subproject_id=${subprojectId}`;
  }

  if (workPeriodIds) {
    apiURL += `&work_period_ids=${workPeriodIds}`;
  }
  if (organizationId) {
    apiURL += `&organization_id=${organizationId}`;
  }

  apiURL += `&force_recalculation=${force_recalculation}`;

  return axiosInstance
    .get<CostByInitiativeV2Response>(apiURL)
    .then((response) => response.data.cost_by_initiative)
    .catch((error) => Promise.reject({ error: error.response }));
}

/**
 * Endpoint to fetch task details for allocation of costs chart
 *
 * @param {string} portfolioId selected portfolio id
 * @param {string} startDate selected start date
 * @param {string} endDate selected end date
 * @param {string} date selected date to filter the tasks
 * @param {string} factor selected factor to filter the tasks
 * @param {string} factorOption selected factor option to filter the tasks
 * @param {string} projectId (OPTIONAL) project id if any selected
 * @param {string} subprojectId (OPTIONAL) subproject id if any selected
 * @param {string} workPeriodId (OPTIONAL) work period id if any selected
 * @param {string} organizationId organization id
 * @returns {MinifiedTask[]} list of task details
 */
function getAllocationOfCostsTaskDetails(
  portfolioId: string,
  startDate: string,
  endDate: string,
  date: string,
  factor: string,
  factorOption: string,
  projectId?: string,
  subprojectId?: string,
  workPeriodId?: string,
  organizationId?: string,
): Promise<MinifiedTask[]> {
  let apiURL =
    baseURL +
    `/api/work-periods/tasks/?measure_name=cost_allocation&portfolio_id=${portfolioId}&start_date=${startDate}&end_date=${endDate}&measure_filter=${date},${factor},${factorOption}&time_allocation_type=monthly`;
  if (projectId !== undefined) {
    apiURL += `&project_id=${projectId}`;
  }
  if (subprojectId !== undefined) {
    apiURL += `&subproject_id=${subprojectId}`;
  }
  if (workPeriodId) {
    apiURL += `&work_period_ids=${workPeriodId}`;
  }
  if (organizationId) {
    apiURL += `&organization_id=${organizationId}`;
  }
  return axiosInstance
    .get<MinifiedTask[]>(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response }));
}

/**
 * Endpoint to fetch task details for cost vs output chart
 *
 * @param {string} portfolioId selected portfolio id
 * @param {string} startDate selected start date
 * @param {string} endDate selected end date
 * @param {string} date selected date to filter the tasks
 * @param {string} factor selected factor to filter the tasks
 * @param {string} projectId (OPTIONAL) project id if any selected
 * @param {string} subprojectId (OPTIONAL) subproject id if any selected
 * @param {string} workPeriodId (OPTIONAL) work period id if any selected
 * @param {string} organizationId organization id
 * @returns {MinifiedTask[]} list of task details
 */
function getCostVsOutputTaskDetails(
  portfolioId: string,
  startDate: string,
  endDate: string,
  date: string,
  factor: string,
  projectId?: string,
  subprojectId?: string,
  workPeriodId?: string,
  organizationId?: string,
): Promise<MinifiedTask[]> {
  let apiURL =
    baseURL +
    `/api/work-periods/tasks/?measure_name=cost_vs_output&portfolio_id=${portfolioId}&start_date=${startDate}&end_date=${endDate}&measure_filter=${date},${factor}&time_allocation_type=monthly`;
  if (projectId !== undefined) {
    apiURL += `&project_id=${projectId}`;
  }
  if (subprojectId !== undefined) {
    apiURL += `&subproject_id=${subprojectId}`;
  }
  if (workPeriodId) {
    apiURL += `&work_period_ids=${workPeriodId}`;
  }
  if (organizationId) {
    apiURL += `&organization_id=${organizationId}`;
  }
  return axiosInstance
    .get<MinifiedTask[]>(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response }));
}

/**
 * Endpoint to fetch task details for cost by initiative chart
 *
 * @param {string} portfolioId selected portfolio id
 * @param {string} startDate selected start date
 * @param {string} endDate selected end date
 * @param {string} factor selected factor to filter the tasks (Initiative ID)
 * @param {string} projectId (OPTIONAL) project id if any selected
 * @param {string} workPeriodId (OPTIONAL) work period id if any selected
 * @param {string} organizationId organization id
 * @returns {MinifiedTask[]} list of task details
 */

function getCostByInitiativeTaskDetails(
  portfolioId: string,
  startDate: string,
  endDate: string,
  factor: string,
  projectId?: string,
  workPeriodId?: string,
  organizationId?: string,
): Promise<MinifiedTask[]> {
  let apiURL =
    baseURL +
    `/api/work-periods/tasks/?measure_name=cost_by_initiative&portfolio_id=${portfolioId}&start_date=${startDate}&end_date=${endDate}&measure_filter=${factor}&time_allocation_type=monthly`;
  if (projectId !== undefined) {
    apiURL += `&project_id=${projectId}`;
  }
  if (workPeriodId) {
    apiURL += `&work_period_ids=${workPeriodId}`;
  }
  if (organizationId) {
    apiURL += `&organization_id=${organizationId}`;
  }
  return axiosInstance
    .get<MinifiedTask[]>(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response }));
}

/**
 * Endpoint to fetch agent spending
 *
 * @param {string} organizationId selected organization id
 * @param {string} years selected years comma separated (optional)
 * @returns {AgentSpending[]} list of agent spending
 */
function getAgentSpending(organizationId: string, years?: string): Promise<AgentSpending[]> {
  let apiURL = baseURL + `/api/financials/agent-spending/?organization_id=${organizationId}`;
  if (years) {
    apiURL += `&years=${years}`;
  }
  return axiosInstance
    .get<AgentSpending[]>(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response }));
}

/**
 * Method for bulk upserting agent spending data
 *
 * @param {AgentSpendingBulkUpsertRequest} payload payload containing organization_id, year, and optional agent_sources data
 * @returns {Promise<AgentSpending[]>} updated agent spending data
 */
function bulkUpsertAgentSpending(payload: AgentSpendingBulkUpsertRequest): Promise<AgentSpending[]> {
  const apiURL = baseURL + `/api/financials/agent-spending/update-by-params/`;
  return axiosInstance
    .put(apiURL, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response }));
}

/**
 * Endpoint to fetch agent costs data for the financials agent costs chart
 *
 * @param {string} organizationId - The organization ID
 * @param {Date} startDate - The start date
 * @param {Date} endDate - The end date
 * @param {string | null} portfolioId - The portfolio ID (optional)
 * @returns {Promise<AgentFinancialResponse>} agent financial cost data
 */
function getAgentCosts(
  organizationId: string,
  startDate: Date,
  endDate: Date,
  portfolioId?: string | null,
): Promise<AgentFinancialResponse> {
  const start_date = formatDate(startDate);
  const end_date = formatDate(endDate);
  let apiURL = `${baseURL}/api/agent-miner/costs/?organization_id=${organizationId}&start_date=${start_date}&end_date=${end_date}`;
  if (portfolioId) {
    apiURL += `&portfolio_id=${portfolioId}`;
  }
  return axiosInstance
    .get<AgentFinancialResponse>(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response }));
}

export {
  bulkUpsertAgentSpending,
  bulkUpsertSpending,
  bulkUpsertSpendingV1,
  getAgentCosts,
  getAgentSpending,
  getAllocationOfCostsChartData,
  getAllocationOfCostsTaskDetails,
  getCostByInitiativeChartData,
  getCostByInitiativeTaskDetails,
  getCostVsOutputChartData,
  getCostVsOutputTaskDetails,
  getSpending,
};
